import { useQuery } from '@apollo/client';

import { getClientName } from 'bundles/cms/utils/SpaceUtils';
import { orgLandingContentfulQuery } from 'bundles/org-landing/components/OrgLandingContentfulQuery';
import {
  orgLandingProgramMembershipsQuery,
  orgLandingProgramsQuery,
} from 'bundles/org-landing/components/OrgLandingProgramsQuery';
import type {
  OrgLandingProgramsQuery,
  OrgLandingProgramsQueryVariables,
  OrgLandingProgramsQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_enterprisePrograms_elements,
  OrgLandingProgramsQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_invitations_elements,
} from 'bundles/org-landing/components/__generated__/OrgLandingProgramsQuery';
import filterExistsOrDefault from 'bundles/program-common/utils/filterExistsOrDefault';
import type {
  OrgLandingContentfulQuery,
  OrgLandingContentfulQueryVariables,
} from 'bundles/program-home/__generated__/contentfulTypes';

import type {
  OrgLandingProgramMembershipsQuery,
  OrgLandingProgramMembershipsQueryVariables,
  OrgLandingProgramMembershipsQuery_ProgramMembershipsV2Resource_multiGet_elements,
} from '../__generated__/OrgLandingProgramMembershipsQuery';

type Options = {
  orgSlug: string;
  userId?: number;
  locale?: string;
  isPreview?: boolean;
  skip?: boolean;
};

export type EnterpriseProgram =
  OrgLandingProgramsQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_enterprisePrograms_elements;

export type ProgramInvitation =
  OrgLandingProgramsQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_invitations_elements;

export type ProgramMembership = OrgLandingProgramMembershipsQuery_ProgramMembershipsV2Resource_multiGet_elements;

export type OutProps = {
  enterprisePrograms: Array<EnterpriseProgram>;
  programInvitations: Array<ProgramInvitation>;
  membershipsMap: Record<string, ProgramMembership>;
  contentfulData?: OrgLandingContentfulQuery;
  isLoading: boolean;
};

function useOrgLandingQuery({ orgSlug, userId, locale = 'en', isPreview = false, skip = false }: Options): OutProps {
  const { data: contentfulData, loading: contentfulLoading } = useQuery<
    OrgLandingContentfulQuery,
    OrgLandingContentfulQueryVariables
  >(orgLandingContentfulQuery, {
    variables: {
      orgSlug,
      preview: isPreview,
      locale,
    },
    skip,
    context: { clientName: getClientName('ENTERPRISE', isPreview) },
  });

  const { data: programsData, loading: isProgramsDataLoading } = useQuery<
    OrgLandingProgramsQuery,
    OrgLandingProgramsQueryVariables
  >(orgLandingProgramsQuery, {
    skip: !userId,
    variables: {
      userId: (userId ?? '').toString(),
      thirdPartyOrgSlug: orgSlug,
    },
  });

  const enterprisePrograms = filterExistsOrDefault(
    programsData?.EnterpriseLearnerSummariesV1Resource?.byUserIdAndThirdPartyOrgSlug?.elements?.[0]?.enterprisePrograms
      ?.elements
  );
  const programInvitations = filterExistsOrDefault(
    programsData?.EnterpriseLearnerSummariesV1Resource?.byUserIdAndThirdPartyOrgSlug?.elements?.[0]?.invitations
      ?.elements
  );

  const programMembershipIds =
    programsData?.EnterpriseLearnerSummariesV1Resource?.byUserIdAndThirdPartyOrgSlug?.elements?.[0]
      ?.programMembershipIds ?? [];

  const { data: programMembershipsData, loading: isProgramMembershipsLoading } = useQuery<
    OrgLandingProgramMembershipsQuery,
    OrgLandingProgramMembershipsQueryVariables
  >(orgLandingProgramMembershipsQuery, {
    skip: programMembershipIds.length === 0,
    variables: {
      ids: programMembershipIds,
    },
  });

  const isLoading = contentfulLoading || isProgramsDataLoading || isProgramMembershipsLoading;

  const membershipsMap: Record<string, ProgramMembership> = {};
  if (!isLoading) {
    for (const membership of programMembershipsData?.ProgramMembershipsV2Resource?.multiGet?.elements ?? []) {
      if (membership) {
        membershipsMap[membership.programId] = membership;
      }
    }
  }

  return { enterprisePrograms, programInvitations, membershipsMap, contentfulData, isLoading };
}

export default useOrgLandingQuery;
